/**
 * Builds a confirmation message for an appointment booking
 * Handles special cases where "Dr." prefix should not be used
 */

interface ConfirmationMessageParams {
  providerName: string;
  meetingDate: Date | string;
  locationId?: string;
}

/**
 * Determines if a provider should have the "Dr." prefix based on location and name
 */
const shouldUseDoctorPrefix = (providerName: string, locationId?: string): boolean => {
  // iOrtho location - specific provider exclusions
  if (locationId === 'HJDZ3RZaiPCPQOoV9ADo') {
    const excludedProviders = ['MRI', 'mri'];
    return !excludedProviders.includes(providerName);
  }

  // Check if provider name already contains a title
  const titlePrefixes = ['Dr.', 'Dr', 'Doctor', 'DDS', 'DMD', 'MD'];
  const hasTitle = titlePrefixes.some((prefix) =>
    providerName.toLowerCase().startsWith(prefix.toLowerCase()),
  );

  if (hasTitle) {
    return false;
  }

  // Check if provider name is likely not a person (e.g., equipment, facility)
  const nonPersonNames = ['MRI', 'X-Ray', 'CT Scan', 'Ultrasound', 'Lab', 'Imaging'];
  const isNonPerson = nonPersonNames.some((name) =>
    providerName.toLowerCase().includes(name.toLowerCase()),
  );

  if (isNonPerson) {
    return false;
  }

  // Default: use "Dr." prefix
  return true;
};

/**
 * Builds the confirmation message for an appointment
 */
export const buildConfirmationMessage = ({
  providerName,
  meetingDate,
  locationId,
}: ConfirmationMessageParams): string => {
  const dateObj = typeof meetingDate === 'string' ? new Date(meetingDate) : meetingDate;
  const formattedDate = dateObj.toLocaleString('en-US');
  const prefix = shouldUseDoctorPrefix(providerName, locationId) ? 'Dr. ' : '';

  return `Your appointment has been confirmed for ${formattedDate} with ${prefix}${providerName}`;
};
